import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Calendar, Clock, User } from 'lucide-react';
import { useQuarterlyStatsDetail, useSubmitQuarterlyStats } from '@/hooks/use-h3comply';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { QuarterlyStatsStatus } from '@/lib/types';

const STATUS_BADGES: Record<QuarterlyStatsStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-800' },
  submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-800' },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800' },
};

export function QuarterlyStatisticsDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuarterlyStatsDetail(Number(id));
  const submitMutation = useSubmitQuarterlyStats();

  if (isLoading) {
    return <div className="flex items-center justify-center py-12 text-gray-500">Loading...</div>;
  }

  if (!stats) {
    return (
      <div className="py-12 text-center text-gray-500">Statistics record not found.</div>
    );
  }

  const badge = STATUS_BADGES[stats.status];

  const handleSubmit = async () => {
    await submitMutation.mutateAsync(stats.id);
  };

  const injuryData = [
    { label: 'Fatalities', value: stats.fatalities, highlight: stats.fatalities > 0 },
    { label: 'Lost Time Injuries', value: stats.lost_time_injuries, highlight: stats.lost_time_injuries > 0 },
    { label: 'Restricted Work Injuries', value: stats.restricted_work_injuries, highlight: false },
    { label: 'Medical Treatment Injuries', value: stats.medical_treatment_injuries, highlight: false },
    { label: 'First Aid Cases', value: stats.first_aid_cases, highlight: false },
  ];

  const safetyData = [
    { label: 'Near Misses Reported', value: stats.near_misses_reported },
    { label: 'Incidents Reported', value: stats.incidents_reported },
    { label: 'Toolbox Talks Conducted', value: stats.toolbox_talks_conducted },
    { label: 'Equipment Failures', value: stats.equipment_failures },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/compliance/statistics')}
            className="rounded-md p-1 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Q{stats.quarter} {stats.year} Statistics
              </h1>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}
              >
                {badge.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {stats.auto_calculated ? 'Auto-generated from system data' : 'Manually entered'}
            </p>
          </div>
        </div>
        {stats.status === 'draft' && (
          <button
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {submitMutation.isPending ? 'Submitting...' : 'Submit to IRATA'}
          </button>
        )}
      </div>

      {/* Submission Info */}
      {stats.submitted_at && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-blue-800">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              Submitted: {formatDateTimeInTimeZone(stats.submitted_at, timeZone, { dateStyle: 'medium' })}
            </div>
            {stats.submitted_by_username && (
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                By: {stats.submitted_by_username}
              </div>
            )}
            {stats.irata_reference && (
              <div>IRATA Ref: {stats.irata_reference}</div>
            )}
          </div>
        </div>
      )}

      {/* Work Volume */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Work Volume</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">
              {Number(stats.total_hours_worked).toLocaleString()}
            </p>
            <p className="mt-1 text-sm text-gray-500">Total Hours Worked</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">
              {Number(stats.total_rope_access_hours).toLocaleString()}
            </p>
            <p className="mt-1 text-sm text-gray-500">Rope Access Hours</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total_technicians}</p>
            <p className="mt-1 text-sm text-gray-500">Technicians</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total_projects}</p>
            <p className="mt-1 text-sm text-gray-500">Projects</p>
          </div>
        </div>
      </div>

      {/* Injury Breakdown */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Injury Breakdown</h2>
        <div className="mt-4 space-y-3">
          {injuryData.map(({ label, value, highlight }) => (
            <div
              key={label}
              className="flex items-center justify-between rounded-md px-4 py-2 odd:bg-gray-50"
            >
              <span className="text-sm text-gray-700">{label}</span>
              <span
                className={`text-sm font-semibold ${
                  highlight ? 'text-red-600' : 'text-gray-900'
                }`}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Safety Metrics */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Safety Metrics</h2>
        <div className="mt-4 space-y-3">
          {safetyData.map(({ label, value }) => (
            <div
              key={label}
              className="flex items-center justify-between rounded-md px-4 py-2 odd:bg-gray-50"
            >
              <span className="text-sm text-gray-700">{label}</span>
              <span className="text-sm font-semibold text-gray-900">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      {stats.notes && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{stats.notes}</p>
        </div>
      )}

      {/* Metadata */}
      <div className="text-xs text-gray-400">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Created: {formatDateTimeInTimeZone(stats.created_at, timeZone)} | Updated:{' '}
          {formatDateTimeInTimeZone(stats.updated_at, timeZone)}
        </div>
      </div>
    </div>
  );
}
